import {
  Alert,
  Chip,
  FormattedDate,
  Heading,
  Section,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  humanize,
} from '@ideon-api/ideon-ui'
import type { RateLimitRow } from '../../lib/use-account-rate-limits'

// vericred's period vocabulary, shortest first; its lifetime period is named `eternity`.
const KNOWN_PERIODS = ['minute', 'hour', 'day', 'month', 'eternity']

function periodLabel(period: string): string {
  return period === 'eternity' ? 'Lifetime' : humanize(period)
}

function periodsOf(row: RateLimitRow): string[] {
  const seen = new Set([
    ...Object.keys(row.limits ?? {}),
    ...Object.keys(row.usage ?? {}),
    ...Object.keys(row.breaches ?? {}),
  ])
  const known = KNOWN_PERIODS.filter((p) => seen.has(p))
  return [...known, ...[...seen].filter((p) => !KNOWN_PERIODS.includes(p)).sort()]
}

// An empty limits object means vericred enforces nothing for this customer; null means it did not say.
// Breach history from earlier limits can outlive the limits, so it never decides this.
function isUnlimited(row: RateLimitRow): boolean {
  return row.limits !== null && Object.keys(row.limits).length === 0
}

function formatCap(cap: number | null | undefined): string {
  if (cap === undefined) return '—'
  return cap === null ? 'Unlimited' : cap.toLocaleString()
}

function PeriodsTable({ row }: { row: RateLimitRow }) {
  const periods = periodsOf(row)
  // Tracking off: counters and breach history do not exist, so showing them would read as a healthy zero.
  const tracked = row.trackingEnabled !== false

  if (periods.length === 0) {
    return isUnlimited(row) ? null : <p className="text-ideon-gray-70">No periods reported for this app id.</p>
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Period</TableHead>
          <TableHead>Limit</TableHead>
          {tracked && <TableHead>Usage</TableHead>}
          {tracked && <TableHead>429 breaches (since tracking began)</TableHead>}
        </TableRow>
      </TableHeader>
      <TableBody>
        {periods.map((period) => {
          const breach = row.breaches?.[period]
          return (
            <TableRow
              key={period}
              data-testid={`period-row-${row.appId}-${period}`}
              className={breach ? 'bg-ideon-state-warning/10' : undefined}
            >
              <TableCell className="font-medium">{periodLabel(period)}</TableCell>
              <TableCell>{formatCap(row.limits?.[period])}</TableCell>
              {tracked && (
                <TableCell>{row.usage?.[period] === undefined ? '—' : row.usage[period].toLocaleString()}</TableCell>
              )}
              {tracked && (
                <TableCell>
                  {breach ? (
                    <span className="flex items-center gap-2">
                      <Chip variant="filled">{breach.count.toLocaleString()}</Chip>
                      {breach.lastAt && (
                        <span className="text-ideon-gray-70">
                          last <FormattedDate value={breach.lastAt} showTime />
                        </span>
                      )}
                    </span>
                  ) : (
                    // Null means vericred could not read Redis: unknown, not "none".
                    (row.breaches === null ? '—' : 'None recorded')
                  )}
                </TableCell>
              )}
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}

const RECENT_BREACH_ROWS_SHOWN = 10

// The breach column counts since tracking began; the hour list does not. Naming the latest breach
// keeps an empty hour from reading as a contradiction.
function latestBreachAt(row: RateLimitRow): string | null {
  const times = Object.values(row.breaches ?? {}).flatMap((breach) => (breach.lastAt ? [breach.lastAt] : []))
  if (times.length === 0) return null
  return times.reduce((latest, time) => (Date.parse(time) > Date.parse(latest) ? time : latest))
}

// Only minutes with a 429 exist, so a quiet hour is one line, not sixty.
function RecentBreaches({ row }: { row: RateLimitRow }) {
  if (row.trackingEnabled === false || row.recentBreaches === null) return null
  if (isUnlimited(row) && row.recentBreaches.length === 0) return null

  const newestFirst = [...row.recentBreaches].reverse()
  const shown = newestFirst.slice(0, RECENT_BREACH_ROWS_SHOWN)
  const hidden = newestFirst.length - shown.length
  const latest = latestBreachAt(row)

  return (
    <div className="mt-4" data-testid={`recent-breaches-${row.appId}`}>
      <p className="mb-2 font-medium">429s in the last hour</p>
      {shown.length === 0 ? (
        <p className="text-ideon-gray-70">
          No 429s in the last hour.
          {latest && (
            <>
              {' '}
              The latest recorded 429 was <FormattedDate value={latest} showTime />.
            </>
          )}
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Minute</TableHead>
              <TableHead>429s</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.map((entry) => (
              <TableRow key={entry.minute} data-testid={`recent-breach-${row.appId}-${entry.minute}`}>
                <TableCell>
                  <FormattedDate value={entry.minute} showTime />
                </TableCell>
                <TableCell>{entry.count.toLocaleString()}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {hidden > 0 && (
        <p className="mt-2 text-ideon-gray-70">
          and {hidden} more {hidden === 1 ? 'minute' : 'minutes'}
        </p>
      )}
    </div>
  )
}

export function AppIdRateLimits({ row }: { row: RateLimitRow }) {
  return (
    <Section data-testid={`app-id-section-${row.appId}`}>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Heading variant="title-m">{row.appId}</Heading>
        {!row.failure && isUnlimited(row) && <Chip variant="outlined">Unlimited</Chip>}
        {row.trackingEnabled === false && <Chip variant="outlined">Tracking off</Chip>}
      </div>

      {row.failure ? (
        <Alert variant="warning" data-testid={`app-id-failure-${row.appId}`}>
          Could not load rate limits for this app id: {humanize(row.failure)}
        </Alert>
      ) : (
        <>
          {row.trackingEnabled === false && (
            <p className="mb-3 text-ideon-gray-70">
              Usage tracking is off for this app id, so only the configured limits are shown.
            </p>
          )}
          {row.trackingEnabled === true && row.usage === null && (
            <Alert variant="warning" className="mb-3" data-testid={`counters-unavailable-${row.appId}`}>
              Live usage and 429 counts are unavailable right now. Only the configured limits are shown.
            </Alert>
          )}
          {isUnlimited(row) && (
            <p className="mb-3 text-ideon-gray-70">
              No rate limits are configured for this app id.
            </p>
          )}
          <PeriodsTable row={row} />
          <RecentBreaches row={row} />
          <p className="mt-3 text-sm text-ideon-gray-70" data-testid={`customer-updated-${row.appId}`}>
            Customer record last updated:{' '}
            {row.customerUpdatedAt ? <FormattedDate value={row.customerUpdatedAt} showTime /> : '—'}
            {' '}(not the last 3Scale sync)
          </p>
        </>
      )}
    </Section>
  )
}
